<template>
  <button
    type="button"
    class="btn btn-sm admin-actions-toggle dropdown-toggle"
    aria-haspopup="true"
    :aria-controls="menuId"
    :disabled="busy"
    @click="menu?.toggle($event)"
  >
    <FontAwesomeIcon v-if="busy" icon="spinner" class="fa-spin me-1" />
    Admin
  </button>
  <Menu
    :id="menuId"
    ref="menu"
    :model="items"
    popup
    :pt="{
      root: { class: 'dropdown-menu show' },
      list: { class: 'list-unstyled mb-0' },
    }"
  >
    <template #item="{ item, props: itemProps }">
      <a v-bind="itemProps.action" class="dropdown-item" :class="item.class">
        {{ item.label }}
      </a>
    </template>
  </Menu>
</template>

<script setup lang="ts">
import type { MenuItem } from "primevue/menuitem";
import Menu from "primevue/menu";
import { computed, useId, useTemplateRef } from "vue";
import type { CompanyOrder } from "../common/companyOrderTypes";
import { OrderStatus, OrderType } from "../common/companyOrderTypes";

/**
 * Admin-only actions on an order, listing only those that apply to its status. The menu is a popup
 * attached to the page body, so a virtually scrolled table cannot clip it.
 */
const props = defineProps<{
  order: CompanyOrder;
  busy: boolean;
}>();

const emit = defineEmits<{
  "update:status": [status: "COMPLETED" | "CANCELLED"];
  retry: [];
}>();

const RETRYABLE_ORDER_TYPES = new Set([
  OrderType.SEND,
  OrderType.SELL,
  OrderType.CANCEL,
]);

const RETRYABLE_STATUSES = new Set([OrderStatus.FAILED, OrderStatus.PENDING]);

const menu = useTemplateRef<InstanceType<typeof Menu>>("menu");
const menuId = useId();

const items = computed<MenuItem[]>(() => [
  ...(props.order.status !== OrderStatus.COMPLETED
    ? [
        {
          label: "Mark as completed",
          command: () => emit("update:status", "COMPLETED"),
        },
      ]
    : []),
  ...(isRetryable.value
    ? [{ label: "Retry order", command: () => emit("retry") }]
    : []),
  ...(props.order.status !== OrderStatus.CANCELLED
    ? [
        {
          label: "Cancel order",
          class: "text-danger",
          command: () => emit("update:status", "CANCELLED"),
        },
      ]
    : []),
]);

const isRetryable = computed(
  () =>
    RETRYABLE_ORDER_TYPES.has(props.order.order_type) &&
    RETRYABLE_STATUSES.has(props.order.status)
);
</script>

<style scoped lang="scss">
.admin-actions-toggle {
  background: var(--phoenix-warning-bg-subtle);
  border: 1px dashed var(--phoenix-warning-border-subtle);
}
</style>
